import { useEffect, useRef, useState } from "react";
import { absUrl, api } from "@/lib/api";
import { Shield, Swords } from "lucide-react";
import ClassSilhouette, { CLASS_THEMES } from "@/components/game/ClassSilhouette";

/**
 * Crossfading scene image. Never blanks between turns — keeps the previous
 * frame visible while the new URL is preloaded off-DOM, then fades the new
 * layer in via opacity transition. On error, silently falls back to the
 * portrait so the panel never shows a broken image icon.
 */
function CrossfadeScene({ sceneUrl, portraitFallback }) {
  const [current, setCurrent] = useState(sceneUrl || portraitFallback || null);
  const [next, setNext] = useState(null);         // URL currently preloading
  const [nextReady, setNextReady] = useState(false);
  const [currentErrored, setCurrentErrored] = useState(false);
  const preloadRef = useRef(null);

  useEffect(() => {
    const target = sceneUrl || portraitFallback || null;
    if (!target || target === current) return;
    // Preload the new image OFF-DOM first, THEN swap. No blank frame.
    const img = new Image();
    preloadRef.current = img;
    setNext(target);
    setNextReady(false);
    img.onload = () => {
      if (preloadRef.current !== img) return;   // stale
      setNextReady(true);
    };
    img.onerror = () => {
      if (preloadRef.current !== img) return;
      // Preload failed — try portrait as a last-ditch fallback so we never show broken image.
      if (portraitFallback && target !== portraitFallback) {
        const fb = new Image();
        preloadRef.current = fb;
        fb.onload = () => {
          if (preloadRef.current !== fb) return;
          setNext(portraitFallback);
          setNextReady(true);
        };
        fb.onerror = () => {
          setNext(null);
          setNextReady(false);
        };
        fb.src = absUrl(portraitFallback);
      } else {
        setNext(null);
        setNextReady(false);
      }
    };
    img.src = absUrl(target);
    return () => { if (preloadRef.current === img) preloadRef.current = null; };
  }, [sceneUrl, portraitFallback, current]);

  // Once the new layer finishes fading in (700ms), promote it to current + clear.
  useEffect(() => {
    if (!nextReady || !next) return;
    const t = setTimeout(() => {
      setCurrent(next);
      setCurrentErrored(false);
      setNext(null);
      setNextReady(false);
    }, 750);
    return () => clearTimeout(t);
  }, [nextReady, next]);

  // If the DOM `<img>` layer errors out (e.g. served cached broken URL), fall back to portrait.
  const handleCurrentError = () => {
    if (currentErrored) return;
    setCurrentErrored(true);
    if (portraitFallback && current !== portraitFallback) {
      setCurrent(portraitFallback);
    }
  };

  if ((!current && !next) || (currentErrored && !next)) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        data-testid="scene-fallback"
        style={{ background: "radial-gradient(ellipse at center, #1a1005 0%, #0a0503 60%, #000 100%)" }}
      >
        <div className="text-center px-6">
          <div className="text-[10px] tracking-[0.4em] ash-text">THE VISTA STIRS</div>
          <div className="font-heading text-2xl gold-text italic mt-2">The realm holds its breath...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Bottom layer: the current frame — stays visible until the new one is ready. */}
      {current && (
        <img
          src={absUrl(current)}
          alt="Scene"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="scene-image"
          onError={handleCurrentError}
        />
      )}
      {/* Top layer: the new frame — starts at opacity 0 and fades in. */}
      {next && (
        <img
          src={absUrl(next)}
          alt="Scene"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out"
          style={{ opacity: nextReady ? 1 : 0 }}
          data-testid="scene-image-next"
          onError={() => setNextReady(false)}
        />
      )}
    </>
  );
}

export default function DualMediaPanel({
  sceneUrl,
  portraitUrl,
  className,
  classKey,
  loadingScene,
  hitKey = 0,
  shieldUntil = null,
  health,        // current HP — drives damage filter
  maxHealth,     // max HP — denominator for damage filter
  equipped,      // {weapon, armor, ring, ...} — drives equipment badges
  itemsCatalog,  // ITEMS dict from /game/state — resolves item_id → metadata
}) {
  // Shield pulse (iter67): true while server-provided revive_shield_until is in the future.
  const [shielded, setShielded] = useState(() => {
    if (!shieldUntil) return false;
    try { return new Date(shieldUntil).getTime() > Date.now(); } catch { return false; }
  });
  useEffect(() => {
    if (!shieldUntil) { setShielded(false); return; }
    const t = new Date(shieldUntil).getTime();
    const remaining = t - Date.now();
    if (remaining <= 0) { setShielded(false); return; }
    setShielded(true);
    const timer = setTimeout(() => setShielded(false), remaining);
    return () => clearTimeout(timer);
  }, [shieldUntil]);

  return (
    <div
      className="grid grid-cols-[1fr_120px] sm:grid-cols-[1fr_160px] lg:grid-cols-[1fr_260px] gap-2 sm:gap-3 lg:gap-4 relative"
      data-testid="dual-media-panel"
    >
      {/* Full-area red vignette flash on incoming damage */}
      {hitKey > 0 && (
        <div
          key={`vignette-${hitKey}`}
          className="hp-hit-vignette pointer-events-none absolute inset-0 z-10"
          data-testid="hp-hit-vignette"
          aria-hidden="true"
        />
      )}

      {/* Scene 16:9 */}
      <div className="scene-frame aspect-video bg-black relative overflow-hidden" data-testid="scene-frame">
        {loadingScene && !sceneUrl && !portraitUrl ? (
          <div className="absolute inset-0 skeleton" data-testid="scene-skeleton" />
        ) : (
          <CrossfadeScene sceneUrl={sceneUrl} portraitFallback={portraitUrl} />
        )}

        {/* Ambient atmospherics — drifting embers + torch flicker + subtle mist.
            Pure CSS, GPU-composited, ~0.5% CPU. Makes every static scene FEEL alive
            without any regen. */}
        <div className="scene-ambient pointer-events-none absolute inset-0" aria-hidden="true">
          {/* Drifting embers */}
          <span className="ember-particle" style={{ left: "8%",  animationDelay: "0s"    }} />
          <span className="ember-particle" style={{ left: "22%", animationDelay: "1.7s"  }} />
          <span className="ember-particle" style={{ left: "38%", animationDelay: "3.2s"  }} />
          <span className="ember-particle" style={{ left: "56%", animationDelay: "0.9s"  }} />
          <span className="ember-particle" style={{ left: "71%", animationDelay: "2.4s"  }} />
          <span className="ember-particle" style={{ left: "88%", animationDelay: "4.1s"  }} />
          {/* Warm torch flicker overlay */}
          <div className="torch-flicker" />
          {/* Slow mist drift */}
          <div className="mist-drift" />
        </div>

        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 100px rgba(0,0,0,0.7)" }} />
      </div>

      {/* Portrait 3:4 — smaller sidebar on mobile so scene stays legible */}
      <div
        key={`portrait-wrap-${hitKey}`}
        className={`scene-frame aspect-[3/4] bg-black relative overflow-hidden ${hitKey > 0 ? "hp-hit-shake" : ""}`}
      >
        <PortraitImage
          portraitUrl={portraitUrl}
          className={className}
          classKey={classKey}
          health={health}
          maxHealth={maxHealth}
          equipped={equipped}
          itemsCatalog={itemsCatalog}
        />
        {shielded && (
          <div className="shield-pulse" data-testid="shield-pulse" aria-hidden="true" />
        )}
        {className && (
          <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2 lg:p-3 bg-gradient-to-t from-black/95 to-transparent">
            <div className="text-[8px] sm:text-[9px] lg:text-[10px] ash-text tracking-[0.25em] sm:tracking-[0.3em]">CLASS</div>
            <div className="font-heading text-sm sm:text-base lg:text-2xl gold-text leading-tight" data-testid="class-name">{className}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Bulletproof hero portrait with layered rendering:
 *
 *   Layer 1 — CLASS SILHOUETTE (always rendered, bundled SVG, zero network).
 *             Guarantees the player NEVER sees an empty/broken portrait state.
 *   Layer 2 — AI PORTRAIT (fades in once loaded; retries + /portrait/repair).
 *   Layer 3 — DAMAGE OVERLAY (red tint / wound cracks based on HP ratio).
 *   Layer 4 — EQUIPMENT BADGES (weapon icon top-right, armor-set top-left).
 *
 * The silhouette is ALWAYS visible. The AI portrait fades on top when it
 * finishes loading. There is no "errored" state that produces alarming text
 * because the base layer is a dignified, class-themed hero bust.
 */
function PortraitImage({ portraitUrl, className, classKey, health, maxHealth, equipped, itemsCatalog }) {
  const [resolvedSrc, setResolvedSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [repairedUrl, setRepairedUrl] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const repairTried = useRef(false);

  const activeUrl = repairedUrl || portraitUrl;

  // ---- AI portrait loader with retry + server-side repair fallback ----
  useEffect(() => {
    setLoaded(false);
    setResolvedSrc(null);
    repairTried.current = false; // reset on URL change so class-swap / new-run can repair again
    if (!activeUrl) return;
    let cancelled = false;
    let timeoutId = null;

    const tryLoad = (n) => {
      const base = absUrl(activeUrl);
      if (!base) return;
      const src = n === 0 ? base : `${base}${base.includes("?") ? "&" : "?"}r=${n}`;
      const probe = new Image();
      probe.onload = () => {
        if (cancelled) return;
        setResolvedSrc(src);
        setLoaded(true);
      };
      probe.onerror = () => {
        if (cancelled) return;
        if (n < 4) {
          const delay = [500, 1500, 3500, 7000][n] || 7000;
          timeoutId = setTimeout(() => tryLoad(n + 1), delay);
        } else if (!repairTried.current) {
          repairTried.current = true;
          // Pass the failing URL so the server can exclude it from the swap pool;
          // otherwise it may return the same URL we're already failing on.
          api.post("/game/portrait/repair", { failing_url: activeUrl })
            .then((r) => {
              if (!cancelled && r?.portrait_url && r.portrait_url !== activeUrl) {
                setRepairedUrl(r.portrait_url);
              }
              // Silent on failure — silhouette stays visible, no error UI.
            })
            .catch(() => { /* silent — silhouette is still visible */ });
        }
        // After all attempts fail, we just let the silhouette carry the panel.
      };
      probe.src = src;
    };

    tryLoad(0);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [activeUrl, attempt, portraitUrl]);

  // ---- Damage state based on HP ratio ----
  const hpRatio = (maxHealth && maxHealth > 0) ? Math.max(0, Math.min(1, (health ?? maxHealth) / maxHealth)) : 1;
  // Three bands: pristine (>=70%), wounded (40-70%), critical (<40%).
  // We layer a red overlay whose opacity scales with missing HP.
  const damageTier = hpRatio >= 0.70 ? "pristine" : hpRatio >= 0.40 ? "wounded" : "critical";
  const damageOverlay = damageTier === "pristine"
    ? { opacity: 0, mix: "normal" }
    : damageTier === "wounded"
      ? { opacity: 0.18, mix: "multiply" }
      : { opacity: 0.35, mix: "multiply" };
  // Desaturate + darken as HP drops — makes the hero visibly suffer.
  const portraitFilter =
    damageTier === "pristine" ? "none" :
    damageTier === "wounded"  ? "saturate(0.75) brightness(0.92) contrast(1.05)" :
                                "saturate(0.55) brightness(0.80) contrast(1.15)";

  // ---- Equipment resolution ----
  const resolveItem = (id) => (id && itemsCatalog && itemsCatalog[id]) ? itemsCatalog[id] : null;
  const weapon = resolveItem(equipped?.weapon);
  const armor  = resolveItem(equipped?.armor);
  const theme  = CLASS_THEMES[(classKey || "").toLowerCase()] || CLASS_THEMES.warrior;

  return (
    <>
      {/* Layer 1 — Class silhouette (ALWAYS visible, bundled SVG) */}
      <ClassSilhouette classKey={classKey} />

      {/* Layer 2 — AI portrait, fades in over silhouette once loaded */}
      {resolvedSrc && (
        <img
          src={resolvedSrc}
          alt={className ? `${className} portrait` : "Portrait"}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="portrait-image"
          loading="eager"
          decoding="async"
          style={{
            opacity: loaded ? 1 : 0,
            transition: "opacity 400ms ease, filter 500ms ease",
            filter: portraitFilter,
          }}
          onError={() => {
            // Silent — silhouette is already underneath, nothing user-visible breaks.
            setLoaded(false);
            setResolvedSrc(null);
            setAttempt((a) => a + 1);
          }}
        />
      )}

      {/* Layer 3a — Damage red overlay (scales with missing HP) */}
      <div
        className="absolute inset-0 pointer-events-none"
        data-testid="portrait-damage-overlay"
        aria-hidden="true"
        style={{
          background: "radial-gradient(ellipse at 50% 55%, rgba(138,3,3,0.9) 0%, rgba(138,3,3,0) 70%)",
          opacity: damageOverlay.opacity,
          mixBlendMode: damageOverlay.mix,
          transition: "opacity 400ms ease",
        }}
      />
      {/* Layer 3b — Critical wound cracks (only below 40% HP) */}
      {damageTier === "critical" && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 140"
          preserveAspectRatio="none"
          aria-hidden="true"
          data-testid="portrait-cracks"
        >
          <path d="M 10 20 L 25 45 L 20 70 L 35 90" stroke="rgba(255,80,80,0.55)" strokeWidth="0.5" fill="none" />
          <path d="M 70 15 L 60 40 L 72 60 L 65 95" stroke="rgba(255,80,80,0.45)" strokeWidth="0.4" fill="none" />
          <path d="M 50 10 L 48 35 L 52 55" stroke="rgba(255,80,80,0.35)" strokeWidth="0.3" fill="none" />
        </svg>
      )}

      {/* Layer 4 — Equipment badges */}
      {weapon && (
        <div
          className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-sm backdrop-blur-sm"
          data-testid="portrait-weapon-badge"
          title={weapon.name}
          style={{
            background: "rgba(10,5,2,0.72)",
            border: `1px solid ${theme.accent}aa`,
            boxShadow: `0 0 8px ${theme.glow}`,
          }}
        >
          <Swords size={10} style={{ color: theme.accent }} />
          <span className="text-[8px] sm:text-[9px] tracking-widest font-heading" style={{ color: "#f0e6d2" }}>
            {weapon.name.length > 10 ? weapon.name.slice(0, 10).toUpperCase() : weapon.name.toUpperCase()}
          </span>
        </div>
      )}
      {armor && (
        <div
          className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-sm backdrop-blur-sm"
          data-testid="portrait-armor-badge"
          title={armor.name}
          style={{
            background: "rgba(10,5,2,0.72)",
            border: `1px solid ${theme.accent}aa`,
            boxShadow: `0 0 8px ${theme.glow}`,
          }}
        >
          <Shield size={10} style={{ color: theme.accent }} />
          <span className="text-[8px] sm:text-[9px] tracking-widest font-heading" style={{ color: "#f0e6d2" }}>
            {armor.name.length > 10 ? armor.name.slice(0, 10).toUpperCase() : armor.name.toUpperCase()}
          </span>
        </div>
      )}

      {/* Critical HP pulse ring — subtle warning the hero is near death */}
      {damageTier === "critical" && (
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          data-testid="portrait-critical-pulse"
          style={{
            boxShadow: "inset 0 0 40px rgba(138,3,3,0.65)",
            animation: "portrait-critical-pulse 1.4s ease-in-out infinite",
          }}
        />
      )}
      <style>{`
        @keyframes portrait-critical-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 0.95; }
        }
      `}</style>
    </>
  );
}
